import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, MapPin, Briefcase, Clock, DollarSign, MessageCircle, Tag } from 'lucide-react';
import { BrickRatingDisplay, BrickRatingInput } from '@/components/BrickRating';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WorkerProfile({ user, token }) {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const [worker, setWorker] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [unitPrices, setUnitPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewData, setReviewData] = useState({
    rating: 0,
    comment: ''
  });
  const [jobData, setJobData] = useState({
    title: '',
    description: '',
    category: '',
    budget: '',
    urgency: 'normal'
  });

  const categoryLabels = {
    plomeria: 'Plomería',
    electricidad: 'Electricidad',
    pintura: 'Pintura',
    albanileria: 'Albañilería',
    ingeniero_civil: 'Ingeniero Civil',
    arquitecto: 'Arquitecto',
    topografia: 'Topografía',
    cerrajeria: 'Cerrajería',
    planos_disenos: 'Planos y Diseños',
    licencias: 'Licencias'
  };

  const unitLabels = {
    servicio: 'Por servicio',
    hora: 'Por hora',
    metro: 'Por metro',
    metro2: 'Por m²',
    unidad: 'Por unidad'
  };

  useEffect(() => {
    fetchData();
  }, [workerId]);

  const fetchData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const workerRes = await axios.get(`${API}/workers/${workerId}`, config);
      setWorker(workerRes.data);

      const reviewsRes = await axios.get(`${API}/reviews/worker/${workerRes.data.user_id}`, config);
      setReviews(reviewsRes.data);

      // Fetch unit prices
      const pricesRes = await axios.get(`${API}/workers/${workerId}/unit-prices`, config);
      setUnitPrices(pricesRes.data);
    } catch (error) {
      toast.error('Error al cargar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleJobRequest = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        ...jobData,
        category: worker.specialties[0],
        budget: parseFloat(jobData.budget),
        location: { address: 'Por definir' }
      };

      const response = await axios.post(`${API}/jobs`, payload, config);
      toast.success('Solicitud enviada exitosamente');
      setShowJobDialog(false);
      navigate(`/job/${response.data.id}`);
    } catch (error) {
      toast.error('Error al enviar solicitud');
    }
  };

  const handleSubmitReview = async () => {
    if (reviewData.rating === 0) {
      toast.error('Por favor selecciona una calificación');
      return;
    }
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/reviews`, {
        job_id: 'general', // For general reviews without a specific job
        worker_id: worker.user_id,
        rating: reviewData.rating,
        comment: reviewData.comment
      }, config);
      
      toast.success('¡Gracias por tu reseña!');
      setShowReviewDialog(false);
      setReviewData({ rating: 0, comment: '' });
      fetchData(); // Refresh to show new review
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar reseña');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Cargando...</p>
      </div>
    );
  }

  if (!worker) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Trabajador no encontrado</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6">
        <div className="container mx-auto">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="text-white hover:text-orange-500 mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="h-6 w-6" />
            Volver
          </button>
          
          <div className="flex gap-4 items-start">
            <Avatar className="h-20 w-20 border-4 border-orange-500" data-testid="worker-avatar">
              <AvatarImage src={worker.profile_image ? `${BACKEND_URL}${worker.profile_image}?auth=${token}` : undefined} />
              <AvatarFallback className="bg-orange-500 text-white text-2xl font-bold">
                {worker.user_id?.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1">
              <h1 className="text-2xl font-bold mb-2" data-testid="worker-name">
                Trabajador Profesional
              </h1>
              <div className="flex items-center gap-2 mb-2">
                <BrickRatingDisplay rating={worker.rating} size="default" showValue={true} />
                <span className="text-slate-300">({worker.total_jobs} trabajos)</span>
              </div>
              <div className="flex items-center text-slate-300">
                <MapPin className="h-4 w-4 mr-1" />
                {worker.location?.address || 'Ubicación no especificada'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Pricing Card */}
        <Card data-testid="pricing-card">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <DollarSign className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Tarifa por Hora</p>
                <p className="text-2xl font-bold text-orange-600">
                  ${worker.hourly_rate?.toLocaleString()}
                </p>
              </div>
              <div className="text-center">
                <Clock className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Tarifa por Día</p>
                <p className="text-2xl font-bold text-orange-600">
                  ${worker.daily_rate?.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* About */}
        <Card data-testid="about-card">
          <CardHeader>
            <CardTitle>Sobre Mí</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 mb-4">{worker.bio}</p>
            <div className="flex items-center gap-2 text-slate-500">
              <Briefcase className="h-5 w-5" />
              <span>{worker.experience_years} años de experiencia</span>
            </div>
          </CardContent>
        </Card>

        {/* Specialties */}
        <Card data-testid="specialties-card">
          <CardHeader>
            <CardTitle>Especialidades</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {worker.specialties?.map((specialty, idx) => (
                <Badge key={idx} className="bg-orange-100 text-orange-700 text-base py-2 px-4">
                  {categoryLabels[specialty] || specialty}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Unit Price Catalog */}
        {unitPrices.length > 0 && (
          <Card data-testid="unit-prices-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-orange-500" />
                Catálogo de Precios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {unitPrices.map((price) => (
                  <div
                    key={price.id}
                    data-testid={`price-item-${price.id}`}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-slate-800">{price.name}</h4>
                        <Badge variant="outline" className="text-xs">
                          {categoryLabels[price.category] || price.category}
                        </Badge>
                      </div>
                      {price.description && (
                        <p className="text-sm text-slate-500">{price.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-orange-600">
                        ${price.price.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-500">
                        {unitLabels[price.unit] || price.unit}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reviews */}
        <Card data-testid="reviews-card">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Reseñas ({reviews.length})</CardTitle>
              <Button
                data-testid="add-review-button"
                onClick={() => setShowReviewDialog(true)}
                variant="outline"
                size="sm"
                className="border-orange-200 text-orange-600 hover:bg-orange-50"
              >
                Dejar Reseña
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {reviews.length === 0 ? (
              <p className="text-slate-500 text-center py-4">Aún no hay reseñas</p>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b border-slate-200 pb-4 last:border-0">
                    <div className="flex items-center gap-2 mb-2">
                      <BrickRatingDisplay rating={review.rating} size="sm" showValue={false} />
                      <span className="text-sm text-slate-500">
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-slate-600">{review.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Button */}
        <Dialog open={showJobDialog} onOpenChange={setShowJobDialog}>
          <DialogTrigger asChild>
            <Button
              data-testid="request-service-button"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-6 rounded-full text-lg"
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Solicitar Servicio
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="job-request-dialog">
            <DialogHeader>
              <DialogTitle>Solicitar Servicio</DialogTitle>
              <DialogDescription>
                Describe el trabajo que necesitas realizar
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Título del Trabajo</Label>
                <Input
                  id="title"
                  data-testid="job-title-input"
                  placeholder="Ej: Reparación de tubería"
                  value={jobData.title}
                  onChange={(e) => setJobData({ ...jobData, title: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  data-testid="job-description-input"
                  placeholder="Describe los detalles del trabajo..."
                  value={jobData.description}
                  onChange={(e) => setJobData({ ...jobData, description: e.target.value })}
                  rows={4}
                />
              </div>
              <div>
                <Label htmlFor="budget">Presupuesto Estimado (COP)</Label>
                <Input
                  id="budget"
                  data-testid="job-budget-input"
                  type="number"
                  placeholder="50000"
                  value={jobData.budget}
                  onChange={(e) => setJobData({ ...jobData, budget: e.target.value })}
                />
              </div>
              <Button
                data-testid="submit-job-request"
                onClick={handleJobRequest}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              >
                Enviar Solicitud
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Review Dialog */}
        <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
          <DialogContent data-testid="review-dialog">
            <DialogHeader>
              <DialogTitle>Calificar Trabajador</DialogTitle>
              <DialogDescription>
                Comparte tu experiencia con este trabajador
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              <div className="text-center">
                <Label className="block mb-3">¿Cómo calificarías el servicio?</Label>
                <div className="flex justify-center">
                  <BrickRatingInput
                    value={reviewData.rating}
                    onChange={(rating) => setReviewData({ ...reviewData, rating })}
                    size="lg"
                  />
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  {reviewData.rating === 0 && "Selecciona los ladrillitos"}
                  {reviewData.rating === 1 && "Muy malo"}
                  {reviewData.rating === 2 && "Malo"}
                  {reviewData.rating === 3 && "Regular"}
                  {reviewData.rating === 4 && "Bueno"}
                  {reviewData.rating === 5 && "¡Excelente!"}
                </p>
              </div>
              <div>
                <Label htmlFor="review-comment">Comentario</Label>
                <Textarea
                  id="review-comment"
                  data-testid="review-comment-input"
                  placeholder="Cuéntanos sobre tu experiencia..."
                  value={reviewData.comment}
                  onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                  rows={4}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid="submit-review"
                  onClick={handleSubmitReview}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  disabled={reviewData.rating === 0}
                >
                  Enviar Reseña
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowReviewDialog(false)}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}